import { Box, Button, Tooltip, useColorModeValue } from '@chakra-ui/react';
import { faLink } from '@fortawesome/free-solid-svg-icons';
import { CellContext, Row } from '@tanstack/react-table';
import { isNil } from 'lodash';
import { ReactNode, useMemo } from 'react';
import invariant from 'tiny-invariant';
import { useTranslation } from 'react-i18next';
import { ReactComponent as DynamicLinkingIcon } from '../../../assets/icons/dynamic-linking.svg';
import SparkelIcon from '../../common/icon/SparkelIcon';
import { isEditableTableMeta, isEditingRow } from '../../table/Table';
import { useProjectHistory } from '../project-history/hooks/useProjectHistory';
import { DiffLinkedElementCount } from '../project-history/DiffLinkedElementCount';
import { TableEntry, getEntryDiffPath } from './OrderEntriesPanel';

export type LinkedCountData = {
  count: number;
  isDynamic: boolean;
};

export type LinkedCountMeta = {
  onSelectLinkedElements: (
    event: React.MouseEvent,
    row: Row<TableEntry>
  ) => void;
};

function isLinkedCountMeta(
  meta: CellContext<TableEntry, LinkedCountData>['column']['columnDef']['meta']
): meta is LinkedCountMeta {
  if (!meta) {
    return false;
  }
  let casted = meta as LinkedCountMeta;
  return !isNil(casted.onSelectLinkedElements);
}

// eslint-disable-next-line complexity
export const LinkedCountCell = ({
  table,
  row,
  column,
  getValue,
}: CellContext<TableEntry, LinkedCountData>): ReactNode => {
  const editingColor = useColorModeValue('gray.200', 'gray.600');
  const standardColor = useColorModeValue('gray.300', 'gray.400');
  const {
    options: { meta },
  } = table;
  invariant(isEditableTableMeta(meta), 'meta is not table meta');
  const columnMeta = column.columnDef.meta;
  invariant(isLinkedCountMeta(columnMeta));
  const isEditingOtherRow = meta.isEditing && !isEditingRow(row, meta);
  const color = isEditingOtherRow ? editingColor : standardColor;
  const value = getValue();

  const { isHistoryDrawerOpen, diff } = useProjectHistory();

  const orderId = row.original.orderId;
  const entryId = row.original.id;
  const rows = table.getRowModel().rows;

  // eslint-disable-next-line complexity
  const linkedElementsDiff = useMemo(() => {
    if (!isHistoryDrawerOpen || !diff) return;
    const subKey = getEntryDiffPath(rows, row.id);
    const linkedElementKey = `project.orders.${orderId}.orderEntries.${entryId}${subKey}.resolvedElementIdentifiersDbid`;
    const oldElementIds: Array<string> = [],
      newElementIds: Array<string> = [];
    const endfix = '.dbIds';
    for (const [key, value] of Object.entries(diff)) {
      if (!key.startsWith(linkedElementKey)) continue;
      for (const p of ['old', 'new']) {
        const arrLocation = key.endsWith(endfix)
          ? value?.[p]
          : value?.[p]?.dbIds;
        // carve out the model urn
        const modelUrn = key.slice(
          linkedElementKey.length,
          key.endsWith(endfix) ? key.length - endfix.length : undefined
        );
        const arr = JSON.parse(arrLocation || '[]') as Array<string>;
        const toInsert = arr.map((dbid) => `${modelUrn}-${dbid}`);
        p === 'new'
          ? newElementIds.push(...toInsert)
          : oldElementIds.push(...toInsert);
      }
    }
    const currentIds = row.original.resolvedElementIdentifiersDbid?.flatMap(
      (elementId) => {
        return elementId.dbIds.map((dbid) => `${elementId.modelUrn}-${dbid}`);
      }
    );
    if (oldElementIds.length || newElementIds.length) {
      return {
        old: oldElementIds,
        new: newElementIds,
        currentIds,
      };
    }
  }, [rows, row, orderId, entryId, isHistoryDrawerOpen, diff]);

  const { t } = useTranslation('table');

  if (value.count > 0 || value.isDynamic)
    return (
      <Tooltip label={t('select-linked-tooltip')} openDelay={400}>
        <Box overflow={'hidden'}>
          <Button
            aria-label={`${row.original.name}-linkedCount`}
            size="xs"
            variant="ghost"
            colorScheme="gray"
            alignSelf={'end'}
            leftIcon={
              <SparkelIcon
                as={
                  value.isDynamic || row.depth > 0
                    ? DynamicLinkingIcon
                    : undefined
                }
                icon={value.isDynamic || row.depth > 0 ? undefined : faLink}
                color={color}
              />
            }
            onClick={(e) => columnMeta.onSelectLinkedElements(e, row)}
            color={color}
            width={'fit-content'}
          >
            {isHistoryDrawerOpen && linkedElementsDiff ? (
              <DiffLinkedElementCount
                oldElements={linkedElementsDiff.old}
                newElements={linkedElementsDiff.new}
                currentIds={linkedElementsDiff.currentIds}
              />
            ) : (
              value.count
            )}
          </Button>
        </Box>
      </Tooltip>
    );
  else {
    return null;
  }
};
